import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { Races } from '../races/Races.js';
import { audioManager } from '../audio/AudioManager.js';

export class Player {
    constructor(scene, camera, world, race, callbacks = {}) {
        this.scene = scene;
        this.camera = camera;
        this.world = world;
        this.race = race;
        this.raceData = Races[race];
        this.callbacks = callbacks;
        this.getEnemies = callbacks.getEnemies || (() => []);
        this.onReloadChange = callbacks.onReloadChange || (() => {});
        this.onDeath = callbacks.onDeath || (() => {});
        
        this.velocity = new THREE.Vector3();
        this.direction = new THREE.Vector3();
        this.moveSpeed = this.raceData.moveSpeed || 5;
        
        const healthMultiplier = callbacks.healthMultiplier || 1.0;
        this.health = Math.floor((this.raceData.health || 100) * healthMultiplier);
        this.maxHealth = this.health;
        this.ammo = 30;
        this.maxAmmo = 30;
        
        // Physics body - use sphere for collision detection
        const shape = new CANNON.Sphere(0.4); // Slightly smaller radius for better collision
        this.body = new CANNON.Body({ mass: 1 });
        this.body.addShape(shape);
        this.body.position.set(0, 1.6, 0);
        this.body.fixedRotation = true;
        this.body.material = new CANNON.Material('player');
        this.body.material.friction = 0.1;
        this.body.linearDamping = 0.9; // Add damping to prevent sliding
        this.world.addBody(this.body);
        
        this.keys = {};
        this.mouseButtons = {};
        this.lastShotTime = 0;
        this.shootCooldown = 0.1; // 10 shots per second
        this.isReloading = false;
        this.reloadStartTime = 0;
        this.lastReloadKeyPress = 0;
        
        // Touch controls
        this.touchMove = { x: 0, y: 0 };
        this.touchShoot = false;
        this.touchReload = false;
        
        // Animation state
        this.weaponSwingTime = 0;
        this.isSwinging = false;
        
        this.setupControls();
        this.setupWeapon();
    }
    
    setupControls() {
        document.addEventListener('keydown', (e) => {
            this.keys[e.code] = true;
        });
        
        document.addEventListener('keyup', (e) => {
            this.keys[e.code] = false;
        });
        
        document.addEventListener('mousedown', (e) => {
            this.mouseButtons[e.button] = true;
        });
        
        document.addEventListener('mouseup', (e) => {
            this.mouseButtons[e.button] = false;
        });
        
        // Mouse look
        document.addEventListener('mousemove', (e) => {
            if (document.pointerLockElement) {
                const sensitivity = 0.002;
                this.camera.rotation.y -= e.movementX * sensitivity;
                this.camera.rotation.x -= e.movementY * sensitivity;
                this.camera.rotation.x = Math.max(
                    -Math.PI / 2,
                    Math.min(Math.PI / 2, this.camera.rotation.x)
                );
            }
        });
    }
    
    // Touch control methods (called from TouchControls component)
    setTouchMove(x, y) {
        this.touchMove.x = x;
        this.touchMove.y = y;
    }
    
    setTouchCameraRotate(deltaX, deltaY) {
        // Apply sensitivity for touch (higher than mouse for better responsiveness)
        const sensitivity = 0.005; // Increased for better touch responsiveness
        this.camera.rotation.y -= deltaX * sensitivity;
        this.camera.rotation.x -= deltaY * sensitivity;
        this.camera.rotation.x = Math.max(
            -Math.PI / 2,
            Math.min(Math.PI / 2, this.camera.rotation.x)
        );
    }
    
    setTouchShoot(shooting) {
        this.touchShoot = shooting;
    }
    
    setTouchReload() {
        this.touchReload = true;
    }
    
    setupWeapon() {
        // Create weapon based on race
        const weaponGroup = new THREE.Group();
        
        if (this.race === 'wizard') {
            // Wizard Staff (magical weapon)
            const staffHandleGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.8, 8);
            const staffHandleMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x3d2817,
                roughness: 0.8
            });
            const staffHandle = new THREE.Mesh(staffHandleGeometry, staffHandleMaterial);
            staffHandle.rotation.z = Math.PI / 2;
            staffHandle.position.set(0.3, 0.1, -0.6);
            weaponGroup.add(staffHandle);
            
            // Staff top (crystal/orb)
            const orbGeometry = new THREE.SphereGeometry(0.1, 16, 16);
            const orbMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xFF00FF,
                emissive: 0xFF00FF,
                emissiveIntensity: 0.8,
                metalness: 0.9,
                roughness: 0.1
            });
            const orb = new THREE.Mesh(orbGeometry, orbMaterial);
            orb.position.set(0.3, 0.5, -0.6);
            weaponGroup.add(orb);
            
            // Magical glow around orb
            const glowGeometry = new THREE.SphereGeometry(0.12, 16, 16);
            const glowMaterial = new THREE.MeshBasicMaterial({ 
                color: 0xFF00FF,
                transparent: true,
                opacity: 0.3
            });
            const glow = new THREE.Mesh(glowGeometry, glowMaterial);
            glow.position.set(0.3, 0.5, -0.6);
            weaponGroup.add(glow);
        } else {
            // Default sword for other races
            // Sword handle
            const handleGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.15, 8);
            const handleMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x654321,
                roughness: 0.8
            });
            const handle = new THREE.Mesh(handleGeometry, handleMaterial);
            handle.rotation.z = Math.PI / 2;
            handle.position.set(0.3, -0.2, -0.6);
            weaponGroup.add(handle);
            
            // Crossguard
            const crossguardGeometry = new THREE.BoxGeometry(0.25, 0.02, 0.02);
            const crossguardMaterial = new THREE.MeshStandardMaterial({ 
                color: 0x888888,
                metalness: 0.7,
                roughness: 0.3
            });
            const crossguard = new THREE.Mesh(crossguardGeometry, crossguardMaterial);
            crossguard.position.set(0.3, -0.2, -0.6);
            weaponGroup.add(crossguard);
            
            // Sword blade
            const bladeGeometry = new THREE.BoxGeometry(0.05, 0.7, 0.01);
            const bladeMaterial = new THREE.MeshStandardMaterial({ 
                color: 0xcccccc,
                metalness: 0.9,
                roughness: 0.1
            });
            const blade = new THREE.Mesh(bladeGeometry, bladeMaterial);
            blade.position.set(0.3, 0.15, -0.6);
            weaponGroup.add(blade);
            
            // Sword tip
            const tipGeometry = new THREE.ConeGeometry(0.025, 0.1, 4);
            const tip = new THREE.Mesh(tipGeometry, bladeMaterial);
            tip.rotation.x = Math.PI / 2;
            tip.position.set(0.3, 0.5, -0.6);
            weaponGroup.add(tip);
        }
        
        this.weapon = weaponGroup;
        this.weapon.position.set(0.5, -0.3, -0.5);
        this.camera.add(this.weapon);
        
        // Animation state
        this.weaponSwingTime = 0;
        this.isSwinging = false;
        this.weaponBaseRotation = { x: 0, y: 0, z: 0 };
    }
    
    update(deltaTime) {
        // Movement - relative to camera direction
        const moveDirection = new THREE.Vector3();
        
        // Keyboard controls
        if (this.keys['KeyW']) {
            // Forward (relative to camera)
            moveDirection.z -= 1;
        }
        if (this.keys['KeyS']) {
            // Backward (relative to camera)
            moveDirection.z += 1;
        }
        if (this.keys['KeyA']) {
            // Left (relative to camera)
            moveDirection.x -= 1;
        }
        if (this.keys['KeyD']) {
            // Right (relative to camera)
            moveDirection.x += 1;
        }
        
        // Touch controls (joystick)
        if (this.touchMove.x !== 0 || this.touchMove.y !== 0) {
            moveDirection.x += this.touchMove.x;
            moveDirection.z += this.touchMove.y;
        }
        
        // Normalize and apply speed
        if (moveDirection.length() > 0) {
            moveDirection.normalize();
            moveDirection.multiplyScalar(this.moveSpeed);
            
            // Get camera's forward direction using Three.js's built-in method
            // This gives us the direction the camera is looking (in world space)
            const forward = new THREE.Vector3();
            this.camera.getWorldDirection(forward);
            forward.y = 0; // Only horizontal movement
            forward.normalize();
            
            // Calculate right direction (perpendicular to forward on horizontal plane)
            // right = up × forward (cross product gives right vector)
            const right = new THREE.Vector3();
            right.crossVectors(new THREE.Vector3(0, 1, 0), forward).normalize();
            
            // Apply movement relative to camera direction
            const rotatedVelocity = new THREE.Vector3();
            rotatedVelocity.addScaledVector(forward, -moveDirection.z); // Forward/backward (W/S)
            rotatedVelocity.addScaledVector(right, moveDirection.x);   // Left/right (A/D)
            rotatedVelocity.y = 0; // Keep on ground
            
            // Apply to physics body - physics engine will handle collisions
            this.body.velocity.x = rotatedVelocity.x;
            this.body.velocity.z = rotatedVelocity.z;
            
            // Keep Y position fixed (no jumping/falling)
            this.body.velocity.y = 0;
            if (this.body.position.y !== 1.6) {
                this.body.position.y = 1.6;
            }
            
            // Sync camera with physics body
            this.camera.position.x = this.body.position.x;
            this.camera.position.y = this.body.position.y;
            this.camera.position.z = this.body.position.z;
        } else {
            // Stop movement
            this.body.velocity.x = 0;
            this.body.velocity.z = 0;
        }
        
        // Keep camera synced with body
        this.camera.position.x = this.body.position.x;
        this.camera.position.y = this.body.position.y;
        this.camera.position.z = this.body.position.z;
        
        // Boundary check - prevent player from leaving the ground area
        // Assuming ground is 100x100 centered at origin, so boundaries are -50 to +50
        const boundary = 48; // Slightly smaller than ground size to keep player on green area
        if (this.body.position.x > boundary) {
            this.body.position.x = boundary;
            this.body.velocity.x = 0;
        }
        if (this.body.position.x < -boundary) {
            this.body.position.x = -boundary;
            this.body.velocity.x = 0;
        }
        if (this.body.position.z > boundary) {
            this.body.position.z = boundary;
            this.body.velocity.z = 0;
        }
        if (this.body.position.z < -boundary) {
            this.body.position.z = -boundary;
            this.body.velocity.z = 0;
        }
        
        // Sync camera after boundary check
        this.camera.position.x = this.body.position.x;
        this.camera.position.z = this.body.position.z;
        
        // Shooting/Attacking with sword (can't shoot while reloading)
        if (!this.isReloading) {
            const currentTime = Date.now() / 1000;
            if ((this.mouseButtons[0] || this.keys['Space'] || this.touchShoot) && 
                currentTime - this.lastShotTime > this.shootCooldown &&
                this.ammo > 0) {
                this.shoot();
                this.lastShotTime = currentTime;
                
                // Sword swing animation
                if (this.weapon) {
                    this.isSwinging = true;
                    this.weaponSwingTime = 0;
                }
            }
        }
        
        // Touch reload
        if (this.touchReload && this.ammo < this.maxAmmo && !this.isReloading) {
            const currentTime = Date.now();
            if (!this.lastReloadKeyPress || currentTime - this.lastReloadKeyPress > 100) {
                this.reload();
                this.lastReloadKeyPress = currentTime;
                this.touchReload = false; // Reset after use
            }
        }
        
        // Update sword swing animation
        if (this.weapon) {
            const currentTime = Date.now() / 1000;
            if (this.isSwinging) {
                this.weaponSwingTime += deltaTime * 15;
                if (this.weaponSwingTime < Math.PI) {
                    // Swing motion
                    const swingAmount = Math.sin(this.weaponSwingTime) * 0.5;
                    this.weapon.rotation.z = swingAmount;
                    this.weapon.rotation.x = swingAmount * 0.3;
                } else {
                    // Reset
                    this.weapon.rotation.z = 0;
                    this.weapon.rotation.x = 0;
                    this.isSwinging = false;
                }
            } else {
                // Weapon idle animation (slight sway)
                const idleTime = currentTime * 2;
                this.weapon.rotation.z = Math.sin(idleTime) * 0.05;
                this.weapon.position.y = -0.3 + Math.sin(idleTime * 1.5) * 0.02;
            }
        }
        
        // Reload (only trigger on key press, not while held)
        if (this.keys['KeyR'] && this.ammo < this.maxAmmo && !this.isReloading) {
            // Check if this is a new key press (not held)
            const currentTime = Date.now();
            if (!this.lastReloadKeyPress || currentTime - this.lastReloadKeyPress > 100) {
                this.reload();
                this.lastReloadKeyPress = currentTime;
            }
        }
    }
    
    shoot() {
        if (this.ammo <= 0) {
            audioManager.playSound('gun_empty');
            return;
        }
        
        this.ammo--;
        this.callbacks.onAmmoChange?.(this.ammo);
        
        // Play sound
        audioManager.playSound('gun_shot', { volume: 0.7 });
        
        // Raycast for hit detection
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera);
        
        // Get all enemy meshes for raycasting
        const enemies = this.getEnemies();
        const enemyMeshes = enemies
            .filter(e => !e.isDead && e.mesh)
            .map(e => e.mesh);
        
        // Raycast against all objects, but prioritize enemies (exclude allies)
        const allObjects = [...enemyMeshes, ...this.scene.children.filter(obj => 
            obj.isMesh && 
            !obj.userData.isEnemy && 
            !obj.userData.isAlly && // Don't hit allies
            obj.userData.isObstacle !== true
        )];
        
        const intersects = raycaster.intersectObjects(allObjects, true);
        
        if (intersects.length > 0) {
            const hit = intersects[0];
            const hitObject = hit.object;
            
            // Check if we hit an enemy
            if (hitObject.userData.isEnemy && hitObject.userData.enemyInstance) {
                const enemy = hitObject.userData.enemyInstance;
                
                // Calculate damage (base damage + random variation)
                const baseDamage = 25;
                const damage = baseDamage + Math.random() * 10;
                
                // Calculate hit direction (from camera to hit point)
                const hitDirection = new THREE.Vector3()
                    .subVectors(hit.point, this.camera.position)
                    .normalize();
                
                // Apply damage to enemy
                enemy.takeDamage(damage, hit.point, hitDirection);
                
                // Play hit sound
                audioManager.playSound('hit_enemy', { 
                    position: hit.point,
                    volume: 0.8
                });
            } else {
                // Hit wall or obstacle
                audioManager.playSound('hit_wall', { 
                    position: hit.point 
                });
            }
        }
    }
    
    reload() {
        // Don't reload if already reloading or ammo is full
        if (this.isReloading || this.ammo >= this.maxAmmo) {
            return;
        }
        
        this.isReloading = true;
        this.reloadStartTime = Date.now();
        this.onReloadChange(true);
        
        // Play reload sound
        audioManager.playSound('gun_reload');
        
        // Reload after 2 seconds
        setTimeout(() => {
            if (this.isReloading) {
                this.ammo = this.maxAmmo;
                this.callbacks.onAmmoChange?.(this.ammo);
                this.isReloading = false;
                this.onReloadChange(false);
            }
        }, 2000); // 2 second reload time
    }
    
    takeDamage(amount) {
        this.health -= amount;
        this.health = Math.max(0, this.health);
        this.callbacks.onHealthChange?.(this.health);
        
        if (this.health <= 0) {
            this.die();
        }
    }
    
    die() {
        if (this.health > 0) return; // Already dead or not dead yet
        
        audioManager.playSound('death_scream');
        this.onDeath();
    }
    
    destroy() {
        if (this.body) {
            this.world.removeBody(this.body);
        }
        if (this.weapon) {
            this.camera.remove(this.weapon);
            this.weapon.geometry.dispose();
            this.weapon.material.dispose();
        }
    }
}
